import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import type { TFunction } from 'i18next';
import { transactionService, TransactionWaitingOn, WaitingOnEntry } from 'app/services/transaction.service';
import { StatusKey } from 'util/workflowStatus';
import { cachedWaitingOn } from 'app/hooks/waitingOnCache';
import { sentryMask } from 'sentryReplayPrivacy';

/** The steps that wait on a reviewer or approver. New waits on the cardholder, and the rest are done. */
export const WAITING_STEPS: ReadonlySet<StatusKey> = new Set<StatusKey>([
  'needsReview',
  'groupApproval',
  'groupApproval1',
  'groupFinalApproval',
  'pendingApproval',
  'pendingApproval1',
  'pendingFinalApproval',
]);

export const fetchWaitingOn = (transactionId: string, status: StatusKey): Promise<TransactionWaitingOn> =>
  cachedWaitingOn(`${transactionId}:${status}`, () =>
    transactionService.getTransactionWaitingOn(transactionId)
  );

const entryHeading = (entry: WaitingOnEntry, step: string | null, t: TFunction): string => {
  if (entry.kind === 'COMPANY') {
    return t(
      step === 'NEEDS_REVIEW'
        ? 'transactionsPage.waitingOn.companyReviewers'
        : 'transactionsPage.waitingOn.companyApprovers'
    );
  }
  return t(
    entry.kind === 'CARD_GROUP'
      ? 'transactionsPage.waitingOn.cardGroup'
      : 'transactionsPage.waitingOn.projectGroup',
    { name: entry.groupName ?? '' }
  );
};

type LoadState = { key: string; data?: TransactionWaitingOn; failed?: boolean };

const WaitingOnContent = ({ state }: { state: LoadState | null }) => {
  const { t } = useTranslation();
  if (!state || (!state.data && !state.failed)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={12} color="inherit" />
        <Typography variant="body2">{t('transactionsPage.waitingOn.loading')}</Typography>
      </Box>
    );
  }
  if (state.failed || !state.data) {
    return <Typography variant="body2">{t('transactionsPage.waitingOn.error')}</Typography>;
  }
  const { step, entries, awaitingClearing } = state.data;
  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, mb: 0.5 }}>
        {t('transactionsPage.waitingOn.title')}
      </Typography>
      {entries.length ? (
        entries.map((entry) => (
          <Box
            key={`${entry.kind}:${entry.groupName}:${entry.level}`}
            sx={{ '&:not(:last-of-type)': { mb: 1 } }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {entryHeading(entry, step, t)}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {entry.people.length ? (
                entry.people.map((name, i) => (
                  <Typography component="li" variant="body2" key={`${name}-${i}`}>
                    {name}
                  </Typography>
                ))
              ) : (
                <Typography component="li" variant="body2">
                  {t('transactionsPage.waitingOn.peopleCount', { count: entry.total })}
                </Typography>
              )}
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2">{t('transactionsPage.waitingOn.nobody')}</Typography>
      )}
      {awaitingClearing && (
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontStyle: 'italic', mt: 1 }}>
          {t('transactionsPage.waitingOn.awaitingClearing')}
        </Typography>
      )}
    </Box>
  );
};

type WaitingOnTooltipProps = {
  transactionId: string;
  status: StatusKey;
  children: React.ReactElement;
};

/** Lists who a transaction is waiting on, fetched the first time the tooltip opens. */
export const WaitingOnTooltip = ({ transactionId, status, children }: WaitingOnTooltipProps) => {
  const key = `${transactionId}:${status}`;
  const [state, setState] = useState<LoadState | null>(null);

  const handleOpen = useCallback(() => {
    fetchWaitingOn(transactionId, status)
      .then((data) => setState({ key, data }))
      .catch((err: unknown) => {
        captureException(err);
        setState({ key, failed: true });
      });
  }, [key, transactionId, status]);

  return (
    <Tooltip
      title={
        // The theme gives every Typography variant a dark colour, which would
        // override the tooltip's white text.
        // Reviewer/approver names: the tooltip is a portal, so it carries its own marker.
        <Box sx={{ '& .MuiTypography-root': { color: 'inherit' } }} {...sentryMask}>
          <WaitingOnContent state={state?.key === key ? state : null} />
        </Box>
      }
      onOpen={handleOpen}
      // Touch devices never hover, so a tap opens it and it stays long enough to read.
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      arrow
    >
      {children}
    </Tooltip>
  );
};
